import React from 'react';
import moment from 'moment-timezone';
import { FiMail, FiCheckCircle, FiClock } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { StatusBadge, statusBadgeTones } from '@components/StatusBadge';
import { InitialsBadge } from '@components/InitialsBadge';

import styles from './LateEarlyLoginAlerts.module.scss';

const formatDiff = (minutes) => {
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return minutes >= 0 ? `+${label}` : `-${label}`;
};

const formatCheckInTime = (timestamp, timeZone) => {
  if (!timestamp?.toDate) return '—';
  const m = timeZone
    ? moment(timestamp.toDate()).tz(timeZone)
    : moment(timestamp.toDate());
  return m.format('h:mm A');
};

// alertStatus is null when loginAlertEnabled was off that day — labeled
// plainly rather than invented as a fake "within grace" state.
const ALERT_STATUS_META = {
  alertSent: {
    label: 'Alert Sent',
    tone: statusBadgeTones.info,
    icon: <FiMail size={11} />,
  },
  acknowledged: {
    label: 'Acknowledged',
    tone: statusBadgeTones.success,
    icon: <FiCheckCircle size={11} />,
  },
};

export const getAttendanceExceptionsColumns = ({
  memberDirectory,
  timeZone,
}) => [
  {
    id: 'employee',
    name: 'Employee',
    cellRenderer: (row) => {
      const member = memberDirectory[row.userUID] || {};
      const name = member.userName || member.userEmailid || row.userUID;
      return (
        <div className={styles.employeeCell}>
          <InitialsBadge label={name} twoLetter />
          <div className={styles.employeeText}>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Semibold}
              color="gray-900"
            >
              {name}
            </Text>
            {member.userEmailid && member.userName && (
              <Text variant={textVariantsEnum.Text_xs} color="gray-500">
                {member.userEmailid}
              </Text>
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: 'scheduledStart',
    name: 'Scheduled Start',
    cellRenderer: (row) => row.policySnapshot?.scheduledStartTime || '—',
  },
  {
    id: 'actualCheckIn',
    name: 'Actual Check-in',
    cellRenderer: (row) => formatCheckInTime(row.firstLoginAt, timeZone),
  },
  {
    id: 'difference',
    name: 'Difference',
    cellRenderer: (row) =>
      row.checkInDiffMinutes == null ? (
        '—'
      ) : (
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Semibold}
          color={row.checkInType === 'late' ? 'error-600' : 'success-600'}
        >
          {formatDiff(row.checkInDiffMinutes)}
        </Text>
      ),
  },
  {
    id: 'type',
    name: 'Type',
    cellRenderer: (row) =>
      row.checkInType ? (
        <StatusBadge
          label={row.checkInType === 'late' ? 'Late' : 'Early'}
          tone={
            row.checkInType === 'late'
              ? statusBadgeTones.error
              : statusBadgeTones.success
          }
          dot
        />
      ) : (
        '—'
      ),
  },
  {
    id: 'status',
    name: 'Status',
    cellRenderer: (row) => {
      const meta = row.alertStatus ? ALERT_STATUS_META[row.alertStatus] : null;
      if (!meta) {
        return (
          <StatusBadge
            label="No Alert Sent"
            tone={statusBadgeTones.default}
            icon={<FiClock size={11} />}
          />
        );
      }
      return (
        <StatusBadge label={meta.label} tone={meta.tone} icon={meta.icon} />
      );
    },
  },
];
